import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Button, CircularProgress, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import React from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { LoginApi } from '../api/LoginApi';
import VerbaScholaAccountCard, { accountBodySx } from '../component/VerbaScholaAccountCard';
import { primaryButtonSx } from '../style/verbaScholaPublicStyles';

type LinkState = 'checking' | 'valid' | 'invalid' | 'unavailable';

// The page a reset email links to. Opening it only checks the link; the password changes when
// the person submits a new one. Links work once and expire (see password_reset_service).
export default function ResetPassword() {
    const history = useHistory();
    const location = useLocation();
    const code = new URLSearchParams(location.search).get('code') ?? '';
    const [linkState, setLinkState] = React.useState<LinkState>(code ? 'checking' : 'invalid');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [mismatch, setMismatch] = React.useState(false);
    const [error, setError] = React.useState('');
    const [isSaving, setIsSaving] = React.useState(false);
    const [retryCount, setRetryCount] = React.useState(0);
    const confirmRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (!code) return;
        let active = true;
        LoginApi.checkResetPassword(code)
            .then(response => { if (active) setLinkState(response.data.valid ? 'valid' : 'invalid'); })
            .catch(() => { if (active) setLinkState('unavailable'); });
        return () => { active = false; };
    }, [code, retryCount]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSaving) return;
        setError('');
        if (password !== confirmPassword) {
            setMismatch(true);
            confirmRef.current?.focus();
            return;
        }
        setIsSaving(true);
        LoginApi.resetPassword(code, password, confirmPassword)
            .then(() => history.replace('/login', { passwordChanged: true }))
            .catch(err => {
                const status = axios.isAxiosError(err) ? err.response?.status : undefined;
                if (status === 498) {
                    setLinkState('invalid');
                } else {
                    const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
                    setError(typeof message === 'string' && status !== undefined && status < 500 ? message : 'We couldn’t save your new password. Try again in a moment.');
                }
            })
            .finally(() => setIsSaving(false));
    };

    if (linkState === 'checking') {
        return (
            <VerbaScholaAccountCard title="Checking your reset link">
                <Stack direction="row" spacing={1.5} alignItems="center" role="status" sx={{ mt: 2 }}>
                    <CircularProgress size={20} aria-hidden />
                    <Typography sx={accountBodySx}>One moment…</Typography>
                </Stack>
            </VerbaScholaAccountCard>
        );
    }

    if (linkState === 'invalid') {
        return (
            <VerbaScholaAccountCard title="This reset link isn’t valid"
                intro="Reset links work once and expire after an hour. Make sure you opened the whole link from your most recent reset email, or request a new one.">
                <Stack spacing={1} sx={{ mt: 3 }}>
                    <Button component={RouterLink} to="/reset_password_email" sx={{ ...primaryButtonSx, minHeight: 48 }}>Request a new link</Button>
                    <Button component={RouterLink} to="/login" variant="text">Back to log in</Button>
                </Stack>
            </VerbaScholaAccountCard>
        );
    }

    if (linkState === 'unavailable') {
        return (
            <VerbaScholaAccountCard title="We couldn't check your reset link"
                intro="The link may still work. Please try checking it again in a moment.">
                <Stack spacing={1} sx={{ mt: 3 }}>
                    <Button onClick={() => { setLinkState('checking'); setRetryCount(count => count + 1); }}
                        sx={{ ...primaryButtonSx, minHeight: 48 }}>Try again</Button>
                    <Button component={RouterLink} to="/login" variant="text">Back to log in</Button>
                </Stack>
            </VerbaScholaAccountCard>
        );
    }

    const passwordAdornment = (
        <InputAdornment position="end">
            <IconButton onClick={() => setShowPassword(value => !value)} edge="end"
                aria-label={showPassword ? 'Hide passwords' : 'Show passwords'} aria-pressed={showPassword}>
                {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <VerbaScholaAccountCard title="Choose a new password" intro="Enter a new password for your VerbaSchola account. You'll use it the next time you log in.">
            {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: '12px' }}>{error}</Alert>}
            <Stack component="form" spacing={2} onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <TextField id="password" label="New password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" autoFocus required fullWidth
                    value={password} onChange={event => { setPassword(event.target.value); setMismatch(false); }}
                    InputProps={{ endAdornment: passwordAdornment }} />
                <TextField id="confirmPassword" label="Confirm new password" name="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required fullWidth
                    value={confirmPassword} onChange={event => { setConfirmPassword(event.target.value); setMismatch(false); }}
                    error={mismatch} helperText={mismatch ? 'The passwords don’t match. Enter the same password twice.' : undefined} inputRef={confirmRef} />
                <Button type="submit" disabled={isSaving} sx={{ ...primaryButtonSx, minHeight: 48 }}>
                    {isSaving
                        ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} sx={{ color: 'inherit' }} aria-hidden /><span>Saving…</span></Stack>
                        : 'Save new password'}
                </Button>
            </Stack>
        </VerbaScholaAccountCard>
    );
}
